import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
  useEffect,
  useRef,
  useLayoutEffect,
} from 'react';
import supabaseClient from '@/api/supabaseClient.ts';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import useLogin from '@/api/useLogin.ts';
import useSendResetPasswordLink from '@/api/useSendResetPasswordLink.ts';
import useResetPassword from '@/api/useResetPassword.ts';
import { User } from '@/types/models/User';
import UserType from '@/_constants/UserType.ts';
import { useThemeContext } from '@/context/ThemeContext.tsx';
import AvailableThemes from '@/_constants/AvailableThemes.ts';
import useUser from '@/api/useUser.ts';

interface AuthContextProps {
  user: User | null;
  session: Session | null;
  supabaseClient: SupabaseClient;
  isAdmin: boolean;
  isManager: boolean;
  logout: () => void;
  isLoading: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isPendingAuthChangeCheck = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const { updateTheme } = useThemeContext();
  const { data: user, isLoading: isLoadingUserData } = useUser(session?.user?.id);

  const isAdmin = useMemo(() => !!(session?.user?.id && user?.type === UserType.ADMIN), [session?.user?.id, user]);
  const isManager = useMemo(() => !!(session?.user?.id && user?.type === UserType.MANAGER), [session?.user?.id, user]);

  const logout = useCallback(() => {
    void supabaseClient.auth.signOut();
  }, []);

  const loginQuery = useLogin();

  const sendResetPasswordLinkQuery = useSendResetPasswordLink();

  const resetPasswordQuery = useResetPassword();

  const checkData = useCallback(async (session: Session | null) => {
    if (isPendingAuthChangeCheck.current) return;

    isPendingAuthChangeCheck.current = true;

    if (!session) {
      setIsLoading(false);
      setSession(null);
      isPendingAuthChangeCheck.current = false;
      return;
    }

    setIsLoading(false);
    setSession(session);
    isPendingAuthChangeCheck.current = false;
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      void checkData(session);
    });

    return () => {
      subscription.unsubscribe();
      setIsLoading(true);
      setSession(null);
    };
  }, []);

  useLayoutEffect(() => {
    updateTheme(user?.type === UserType.ADMIN ? AvailableThemes.ADMIN : AvailableThemes.DEFAULT);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        session,
        supabaseClient,
        isAdmin,
        isManager,
        logout,
        isLoading: isLoading || isLoadingUserData,
        loginQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}
